import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Grid,
  TextField,
  Typography,
  Card,
  CardContent,
  Container,
  Button,
  Stack,
  Chip,
  IconButton,
  Skeleton,
  InputAdornment,
  Tooltip,
  Divider,
  Alert,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import AddIcon from "@mui/icons-material/Add";
import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import PhoneIphoneIcon from "@mui/icons-material/PhoneIphone";
import BadgeIcon from "@mui/icons-material/Badge";
import * as customerServices from "../../services/customer";
import img from "../../images/profile_example.jpg";
import { useNavigate } from "react-router-dom";
import AppBreadcrumbs from "../../components/AppBreadcrumbs";

// function maskCitizenId(id) {
//   if (!id) return "-";
//   const s = String(id);
//   // 1-2345-67890-12-3 -> แสดงต้น/ท้าย นอกนั้นเป็น *
//   return s.length >= 13
//     ? `${s.slice(0, 1)}-${"".padEnd(4, "*")}-${"".padEnd(5, "*")}-${"".padEnd(2, "*")}-${s.slice(-1)}`
//     : s.replace(/.(?=.{3})/g, "*");
// }

function maskCitizenId(id) {
  if (!id) return "-";
  const s = String(id).replace(/\D/g, "");

  // ครบ 13 หลัก: จัดรูปแบบ 1-2345-67890-12-3
  if (s.length === 13) {
    return `${s.slice(0, 1)}-${s.slice(1, 5)}-${s.slice(5, 10)}-${s.slice(10, 12)}-${s.slice(12)}`;
  }

  return s;
}

function maskPhone(p) {
  if (!p) return "-";
  const s = String(p);
  return s.length >= 9 ? `${s.slice(0, 3)}-${s.slice(3, 6)}-${s.slice(6)}` : s;
}

export default function SaleCustomerListPage() {
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all"); // all | normal | corporate
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [err, setErr] = useState(null);
  const navigate = useNavigate();

  // debounce search
  const [debounced, setDebounced] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 250);
    return () => clearTimeout(t);
  }, [search]);

  useEffect(() => {
    fetchCustomer();
  }, []);

  const fetchCustomer = async () => {
    try {
      setIsLoading(true);
      setErr(null);
      const res = await customerServices.getCustomers("SELLER");
      const rawData = res?.data?.data || [];
      // ป้องกัน null/undefined field
      const safe = rawData.map((c) => ({
        id: c.id,
        name: c.name ?? "-",
        personType: c.personType ?? "normal",
        img: c.img ?? null,
        phone: c.phone ?? "",
        citizenId: c.citizenId ?? "",
        group: c.group ?? "", // ถ้ามี
      }));
      setCustomers(safe);
    } catch (e) {
      console.error("Error fetching customers:", e);
      setErr(e?.message || "โหลดรายการซัพพลายเออร์ไม่สำเร็จ");
    } finally {
      setIsLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const q = debounced.trim().toLowerCase();
    let list = customers;

    if (filterType !== "all") {
      list = list.filter((c) => (c.personType || "normal") === filterType);
    }
    if (q) {
      list = list.filter((c) => {
        const name = (c.name || "").toLowerCase();
        const phone = (c.phone || "").toLowerCase();
        const cid = (c.citizenId || "").toLowerCase();
        return name.includes(q) || phone.includes(q) || cid.includes(q);
      });
    }
    return list;
  }, [customers, debounced, filterType]);

  const handleSelect = (customer) => {
    // ไปหน้าแก้ไขซัพพลายเออร์ขาย (ปรับ path ให้ตรงกับโปรเจกต์จริงถ้าจำเป็น)
    navigate(`/sale-customers/edit?customer_id=${customer.id}`);
  };

  const handleCreateClick = () => {
    navigate(`/sale-customers/create?sale=true`);
  };

  const PersonBadge = ({ type }) => {
    const isNormal = (type || "normal") === "normal";
    return (
      <Chip
        size="small"
        icon={isNormal ? <PersonIcon /> : <BusinessIcon />}
        label={isNormal ? "บุคคลธรรมดา" : "นิติบุคคล"}
        color={isNormal ? "success" : "info"}
        variant="soft"
        sx={{ mr: 1 }}
      />
    );
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <AppBreadcrumbs />

      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        justifyContent="space-between"
        spacing={2}
        mt={2}
        mb={2}
      >
        <Typography variant="h5" fontWeight={800}>
          เลือกซัพพลายเออร์
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" onClick={fetchCustomer}>
            รีเฟรช
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            ซัพพลายเออร์ใหม่
          </Button>
        </Stack>
      </Stack>

      {/* Search + Filter */}
      <Stack direction={{ xs: "column", md: "row" }} spacing={2} mb={3}>
        <TextField
          placeholder="ค้นหาชื่อ / เบอร์โทร / เลขบัตร"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
            endAdornment: search ? (
              <InputAdornment position="end">
                <Tooltip title="ล้างการค้นหา">
                  <IconButton size="small" onClick={() => setSearch("")}>
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </InputAdornment>
            ) : null,
          }}
          sx={{
            width: 400,
          }}
        />
        <ToggleButtonGroup
          exclusive
          size="small"
          value={filterType}
          onChange={(_, v) => v && setFilterType(v)}
          color="primary"
          sx={{ alignSelf: { xs: "stretch", md: "center" } }}
        >
          <ToggleButton value="all">ทั้งหมด</ToggleButton>
          <ToggleButton value="normal">บุคคลธรรมดา</ToggleButton>
          <ToggleButton value="corporate">นิติบุคคล</ToggleButton>
        </ToggleButtonGroup>
      </Stack>

      {err && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {err}
        </Alert>
      )}

      {/* Loading skeleton */}
      {isLoading ? (
        <Grid container spacing={2}>
          {Array.from({ length: 6 }).map((_, i) => (
            <Grid item xs={12} sm={12} md={6} key={i}>
              <Card sx={{ display: "flex", height: 150, p: 0 }}>
                <Skeleton variant="rectangular" width={150} height={150} />
                <CardContent sx={{ flex: 1 }}>
                  <Skeleton width="40%" />
                  <Skeleton width="60%" />
                  <Skeleton width="30%" />
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      ) : filtered.length === 0 ? (
        // Empty state
        <Box
          sx={{
            textAlign: "center",
            py: 6,
            px: 2,
            borderRadius: 3,
            border: "1px dashed",
            borderColor: "divider",
            bgcolor: "background.paper",
          }}
        >
          <Typography variant="h6" fontWeight={700} gutterBottom>
            ไม่พบซัพพลายเออร์ที่ตรงเงื่อนไข
          </Typography>
          <Typography color="text.secondary" mb={2}>
            ลองปรับคำค้น หรือสร้างซัพพลายเออร์ใหม่ได้เลย
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCreateClick}
          >
            สร้างซัพพลายเออร์ใหม่
          </Button>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((customer) => (
            <Grid item xs={12} sm={12} md={6} key={customer.id}>
              <Card
                onClick={() => handleSelect(customer)}
                sx={{
                  display: "flex",
                  cursor: "pointer",
                  bgcolor: "background.paper",
                  borderLeft: "5px solid",
                  borderColor:
                    (customer.personType || "normal") === "normal"
                      ? "success.main"
                      : "info.main",
                  "&:hover": {
                    boxShadow: 4,
                    transform: "translateY(-2px)",
                    transition: "all .15s ease-in-out",
                  },
                  height: 160,
                }}
              >
                {/* รูปภาพซัพพลายเออร์ */}
                {console.log(customer.img)}
                <Box
                  component="img"
                  src={customer.img ? customer.img : img}
                  alt={customer.name}
                  sx={{
                    width: 150,
                    height: "100%",
                    objectFit: "cover",
                    borderRight: "1px solid",
                    borderColor: "divider",
                  }}
                />

                {/* ข้อมูลซัพพลายเออร์ */}
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography fontWeight={800} fontSize={18} noWrap>
                      {customer.name}
                    </Typography>
                    <PersonBadge type={customer.personType} />
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={2}
                    sx={{ mt: 1 }}
                    flexWrap="wrap"
                  >
                    <Stack direction="row" spacing={1} alignItems="center">
                      <PhoneIphoneIcon fontSize="small" />
                      <Typography variant="body2">
                        {maskPhone(customer.phone)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <BadgeIcon fontSize="small" />
                      <Typography variant="body2" color="text.secondary">
                        {maskCitizenId(customer.citizenId)}
                      </Typography>
                    </Stack>
                  </Stack>

                  {customer.group ? (
                    <>
                      <Divider sx={{ my: 1 }} />
                      <Typography variant="body2" color="text.secondary">
                        กลุ่ม: {customer.group}
                      </Typography>
                    </>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Container>
  );
}
